import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding, Input, OnChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, EventSwitcherConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { WhenOverflowedDirective, WhenOverflowedOptions } from '@frontend/sports/common/ui/overflow';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { BehaviorSubject, Subject, distinctUntilChanged, filter, map, skip, switchMap } from 'rxjs';

import { EventSwitcherOnboardingComponent } from '../event-switcher-onboarding/event-switcher-onboarding.component';
import { EventSwitcherToggleHelperEpcotService } from '../services/toggle-helper/event-switcher-toggle-helper-epcot.service';
import { EventSwitcherToggleHelperRowService } from '../services/toggle-helper/event-switcher-toggle-helper-row.service';
import { IEventSwitcherToggleHelperService } from '../services/toggle-helper/event-switcher-toggle-helper.service';

@Component({
    selector: 'ms-event-switcher-toggle',
    templateUrl: './event-switcher-toggle.component.html',
    imports: [CommonModule, CompetitionLogoComponent, EventSwitcherOnboardingComponent, WhenOverflowedDirective, IconCustomComponent],
    providers: [EventSwitcherToggleHelperEpcotService, EventSwitcherToggleHelperRowService],
})
export class EventSwitcherToggleComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() mediaCount: number;
    @Input() inOverlay: boolean;

    @HostBinding('class') className = 'event-switcher-toggle';

    @HostBinding('class.disabled') get isDisabled(): boolean {
        return !this.config.isEnabled || this.eventCount < 2;
    }

    @HostBinding('class.expanded') expanded = false;
    @ViewChild(WhenOverflowedDirective, { read: WhenOverflowedDirective }) directive: WhenOverflowedDirective;

    toggleSwitcher(): void {
        if (this.isDisabled) {
            return;
        }

        this.eventSwitcherToggleHelper.openSwitcher(this.event, this.inOverlay);
    }

    title: string;
    showLeagueLogo: boolean;
    epcotEnabled: boolean;
    overflowOptions: WhenOverflowedOptions = {
        classNameToAdd: 'text-overflows',
        resizeFont: true,
        minFontSize: 9,
        listenToResize: true,
        manualMutationCheck: true,
        adjustRatio: 0.9,
    };

    private eventCount = 0;

    private event$ = new BehaviorSubject<EventModel | undefined>(undefined);
    private mediaTabCount$ = new Subject<number>();

    private eventSwitcherToggleHelper: IEventSwitcherToggleHelperService;

    constructor(
        private config: EventSwitcherConfig,
        private changeDetectorRef: ChangeDetectorRef,
        private epcotConfig: EpcotConfig,
        epcotHelper: EventSwitcherToggleHelperEpcotService,
        rowHelper: EventSwitcherToggleHelperRowService,
    ) {
        this.eventSwitcherToggleHelper = epcotConfig.isEnabled ? epcotHelper : rowHelper;
        this.epcotEnabled = epcotConfig.isEnabled;

        this.event$
            .pipe(
                filter(isDefined),
                switchMap((event) =>
                    this.eventSwitcherToggleHelper.getCount(event).pipe(
                        map((count) => ({
                            event,
                            count,
                        })),
                    ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe(({ event, count }) => this.setModel(event, count));

        this.mediaTabCount$.pipe(skip(1), distinctUntilChanged(), takeUntilDestroyed()).subscribe((_) => {
            this.directive.forceRefresh();
        });

        this.eventSwitcherToggleHelper.expanded$?.pipe(takeUntilDestroyed()).subscribe((expanded) => {
            this.expanded = expanded;
            this.changeDetectorRef.markForCheck();
        });
    }

    ngOnChanges(changes: ISimpleChanges<EventSwitcherToggleComponent>): void {
        if (changes.event?.currentValue) {
            this.event$.next(changes.event.currentValue);
        }

        if (changes.mediaCount?.currentValue) {
            this.mediaTabCount$.next(changes.mediaCount.currentValue);
        }
    }

    get showOnboarding(): boolean {
        return !this.epcotConfig.isEnabled && !this.isDisabled && !this.expanded;
    }

    private setModel(event: EventModel, count: number): void {
        this.showLeagueLogo = this.config.toggleIconAllowedSports.includes(event.sport.id);
        this.eventCount = count;
        this.title = event.league.name + (this.isDisabled || !this.epcotConfig.isEnabled ? '' : ` (${this.eventCount})`);
        this.changeDetectorRef.markForCheck();
    }
}
